import api from './api';

// Types
export interface SocialAccount {
  id: string;
  platform: 'instagram' | 'instagram_reels' | 'facebook' | 'twitter' | 'linkedin' | 'youtube' | 'youtube_shorts' | 'tiktok' | 'threads' | 'facebook_ads' | 'google_ads' | 'linkedin_ads';
  username: string;
  account_id?: string;
  profile_image?: string;
  followers: number;
  following?: number;
  posts_count?: number;
  connected: boolean;
  last_sync?: string;
  token_expires_at?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface SocialPost {
  id: string;
  user_id: string;
  content: string;
  media_urls: string[];
  platforms: string[];
  hashtags: string[];
  status: 'suggested' | 'draft' | 'scheduled' | 'published' | 'failed' | 'partial' | 'processing' | 'archived';
  scheduled_at?: string;
  suggested_time?: string;
  suggested_platforms?: string[];
  published_at?: string;
  external_ids?: Record<string, string>;
  error_message?: string;
  metrics?: {
    likes: number;
    comments: number;
    shares: number;
    saves?: number;
    reach: number;
    impressions?: number;
    engagement_rate: number;
  };
  ai_generated: boolean;
  publish_results?: {
    success?: Array<{ platform: string; externalId?: string; url?: string; skipped?: boolean }>;
    failed?: Array<{ platform: string; error: string }>;
    [key: string]: any;
  } | Record<string, any>;
  content_type?: 'post' | 'story' | 'reel' | 'carousel' | 'video';
  first_comment?: string;
  location?: string;
  link_url?: string;
  social_account_id?: string;
  metadata?: Record<string, any>;
  boost_status?: 'none' | 'pending' | 'active' | 'paused' | 'completed' | 'rejected' | 'failed';
  boost_budget?: number;
  boost_duration_days?: number;
  boost_goal?: string;
  boost_metrics?: {
    impressions: number;
    reach: number;
    clicks: number;
    spend: number;
    ctr: number;
    cpc: number;
  };
  boosted_at?: string;
  boost_end_date?: string;
  created_at: string;
  updated_at?: string;
}

export interface SocialStats {
  totalFollowers: number;
  followersGrowth: number;
  totalPosts: number;
  scheduledPosts: number;
  avgEngagement: number;
}

export type SocialReplyPlatform = 'threads' | 'instagram' | 'facebook' | 'youtube' | 'tiktok';

export interface ThreadsReply {
  id: string;
  text: string;
  username: string;
  timestamp: string;
  post_id: string;
  post_preview: string;
  /** Generic post URL (preferred). */
  post_url?: string | null;
  external_post_id?: string | null;
  /** Legacy fields kept for backwards compatibility with the Threads-only endpoint. */
  threads_post_id?: string | null;
  threads_post_url?: string | null;
  /** Originating platform — defaults to 'threads' when missing. */
  platform?: SocialReplyPlatform;
}

export type SocialReply = ThreadsReply;

export interface SocialPlatformStats {
  replies_count: number;
  posts_count: number;
  failed_posts: number;
  account: { username: string } | null;
  token_expired: boolean;
}

export interface PlatformMetrics {
  platform: string;
  followers: number;
  followersChange: number;
  engagement: number;
  engagementChange: number;
  reach: number;
  reachChange: number;
  impressions: number;
  impressionsChange: number;
}

export interface AISuggestion {
  id: string;
  content: string;
  image_url?: string;
  media_urls?: string[];
  platforms: string[];
  suggested_platforms?: string[];
  suggested_time: string;
  topic?: string;
  hashtags: string[];
  tone?: string;
  content_type?: string;
  media_hint?: string;
  post_type?: string;
  first_comment?: string | null;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface PostFilters {
  page?: number;
  limit?: number;
  status?: string;
  platform?: string;
  search?: string;
}

export interface GenerateCaptionParams {
  topic: string;
  tone?: string;
  platform?: string;
  platforms?: string[];
  keywords?: string[];
  language?: string;
  charLimit?: number;
  ignorePattern?: boolean;
  variationDirection?: string;
}

export interface GenerateImageParams {
  prompt: string;
  style?: 'realistic' | 'artistic' | 'minimal' | 'vibrant' | 'professional';
  aspectRatio?: '1:1' | '4:5' | '16:9' | '9:16';
  platform?: string;
  referenceImageUrl?: string;
}

export interface GenerateVideoParams {
  prompt: string;
  duration?: 15 | 30 | 60;
  style?: 'cinematic' | 'dynamic' | 'minimal' | 'professional';
  aspectRatio?: '1:1' | '4:5' | '9:16' | '16:9';
  platform?: string;
}

export interface AIGeneratedMedia {
  id: string;
  url: string;
  thumbnailUrl?: string;
  type: 'image' | 'video';
  prompt: string;
  width?: number;
  height?: number;
  duration?: number;
  status: 'generating' | 'ready' | 'failed';
  createdAt: string;
}

export interface CreatePostData {
  content: string;
  platforms: string[];
  scheduled_at?: string;
  media_urls?: string[];
  hashtags?: string[];
  status?: 'draft' | 'scheduled';
  content_type?: 'post' | 'story' | 'reel' | 'carousel' | 'video';
  first_comment?: string;
  location?: string;
  link_url?: string;
  metadata?: {
    youtube?: {
      title?: string;
      tags?: string;
      category?: string;
      visibility?: 'public' | 'unlisted' | 'private';
    };
    instagram?: {
      alt_text?: string;
      first_comment?: string;
    };
    linkedin?: {
      article_mode?: boolean;
    };
    twitter?: {
      thread_mode?: boolean;
    };
    tiktok?: {
      duet_enabled?: boolean;
      stitch_enabled?: boolean;
    };
    logo?: {
      url?: string;
      position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
      size?: number;
      enabled?: boolean;
    };
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Social Service
export const socialService = {
  // Accounts
  async getAccounts(projectId?: string): Promise<ApiResponse<SocialAccount[]>> {
    const params = projectId ? { project_id: projectId } : {};
    const response = await api.get('/social/accounts', { params });
    return response.data;
  },

  async getAccountsWithAds(projectId?: string): Promise<ApiResponse<SocialAccount[]>> {
    const params: Record<string, string> = { include_ads: 'true' };
    if (projectId) params.project_id = projectId;
    const response = await api.get('/social/accounts', { params });
    return response.data;
  },

  async connectAccount(data: {
    platform: string;
    username: string;
    access_token: string;
    refresh_token?: string;
  }): Promise<ApiResponse<SocialAccount>> {
    const response = await api.post('/social/accounts', data);
    return response.data;
  },

  async disconnectAccount(accountId: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/social/accounts/${accountId}`);
    return response.data;
  },

  async syncAccount(accountId: string): Promise<ApiResponse<SocialAccount>> {
    const response = await api.post(`/social/accounts/${accountId}/sync`);
    return response.data;
  },

  // Posts
  async getPosts(filters?: PostFilters): Promise<PaginatedResponse<SocialPost>> {
    const response = await api.get('/social/posts', { params: filters });
    return response.data;
  },

  async getPost(id: string): Promise<ApiResponse<SocialPost>> {
    const response = await api.get(`/social/posts/${id}`);
    return response.data;
  },

  async createPost(data: CreatePostData): Promise<ApiResponse<SocialPost>> {
    const response = await api.post('/social/posts', data);
    return response.data;
  },

  async updatePost(id: string, data: Partial<CreatePostData>): Promise<ApiResponse<SocialPost>> {
    const response = await api.put(`/social/posts/${id}`, data);
    return response.data;
  },

  async deletePost(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/social/posts/${id}`);
    return response.data;
  },

  async publishPost(id: string): Promise<ApiResponse<SocialPost> & { async?: boolean; jobId?: string }> {
    const response = await api.post(`/social/posts/${id}/publish`);
    return response.data;
  },

  /**
   * Retry a failed or partial post.
   * Pass `platforms` to re-publish ONLY those (idempotent — won't duplicate
   * platforms that already succeeded). Omit to retry everything from scratch.
   */
  async retryPost(id: string, platforms?: string[]): Promise<ApiResponse<SocialPost>> {
    const body = Array.isArray(platforms) && platforms.length > 0 ? { platforms } : {};
    const response = await api.post(`/social/posts/${id}/retry`, body);
    return response.data;
  },

  // Aggregated social replies across Threads + Instagram + Facebook.
  async getSocialReplies(platforms?: SocialReplyPlatform[]): Promise<ApiResponse<SocialReply[]> & {
    platforms?: Record<string, SocialPlatformStats>;
    posts_count?: number;
    failed_posts?: number;
    account?: { username: string } | null;
    code?: string;
  }> {
    const params = platforms?.length ? { platforms: platforms.join(',') } : undefined;
    const response = await api.get('/social/replies', { params });
    return response.data;
  },

  // Reply to a comment on the given platform (threads/instagram/facebook).
  async replyToSocialComment(
    platform: SocialReplyPlatform,
    commentId: string,
    message: string
  ): Promise<ApiResponse<{ replyId: string; platform: string }>> {
    const response = await api.post(`/social/replies/${platform}/${commentId}/reply`, { message });
    return response.data;
  },

  // Legacy Threads-only helpers — kept for any caller still using them.
  async getThreadsReplies(): Promise<ApiResponse<ThreadsReply[]> & {
    posts_count?: number;
    failed_posts?: number;
    account?: { username: string } | null;
    code?: string;
  }> {
    const response = await api.get('/social/threads/replies');
    return response.data;
  },

  async replyToThreadsComment(commentId: string, message: string): Promise<ApiResponse<{ replyId: string; platform: string }>> {
    const response = await api.post(`/social/threads/replies/${commentId}/reply`, { message });
    return response.data;
  },

  // Stats
  async getStats(): Promise<ApiResponse<SocialStats>> {
    const response = await api.get('/social/stats');
    return response.data;
  },

  async getAnalytics(params?: { period?: '7d' | '30d' | '90d' }): Promise<ApiResponse<{
    platformMetrics: PlatformMetrics[];
    topPosts: SocialPost[];
    overview: {
      totalFollowers: number;
      avgEngagement: number;
      totalReach: number;
      totalImpressions: number;
    };
  }>> {
    const response = await api.get('/social/analytics', { params });
    return response.data;
  },

  // AI Features
  async generateCaption(params: GenerateCaptionParams): Promise<ApiResponse<{
    caption: string;
    hashtags: string[];
    platformFields?: {
      youtubeTitle?: string;
      youtubeTags?: string;
      instagramFirstComment?: string;
      instagramAltText?: string;
    };
  }>> {
    const response = await api.post('/social/generate-caption', params);
    return response.data;
  },

  /**
   * Generate a batch of images for an Instagram carousel (2-10 slides).
   *
   * Always runs async (backend enqueues a BullMQ job) and polls via the
   * ai-job pipeline. Carousel batches can take several minutes — a sync HTTP
   * call would time out at Cloudflare (~100s).
   *
   * Returns a list of S3 URLs once the job is complete — no gallery records.
   */
  async generateCarouselImages(
    params: {
      prompt: string;
      count: number;
      style?: string;
      platform?: string;
    },
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<{ urls: string[]; failed: number; slides?: string[] }>> {
    const { pollJobUntilComplete } = await import('./aiJobService');
    const response = await api.post('/social/carousel/generate-images', params);
    const result = response.data;

    if (result?.async && result.jobId) {
      const jobResult: any = await pollJobUntilComplete(
        result.jobId,
        onProgress,
        2000,     // poll every 2s
        600000    // 10-minute ceiling (covers worst case: 10 images × ~30-60s)
      );
      const payload = jobResult?.data ?? jobResult;
      return {
        success: true,
        data: payload
      } as ApiResponse<{ urls: string[]; failed: number; slides?: string[] }>;
    }

    // Legacy/sync fallback (shouldn't happen now, but keep safe)
    return result;
  },

  async generateSuggestions(params?: {
    count?: number;
    platforms?: string[];
    topics?: string[];
    async?: boolean;
    ignorePattern?: boolean;
    variationDirection?: string;
    generateImages?: boolean;
    imageStyle?: string;
    aspectRatio?: string;
    tone?: string;
    referenceImageUrl?: string;
    mentions?: string[];
  }): Promise<ApiResponse<AISuggestion[]> & { async?: boolean; jobId?: string }> {
    const { referenceImageUrl, ...rest } = params || {};
    const response = await api.post('/social/suggestions/generate', {
      ...rest,
      async: params?.async ?? true, // Default to async mode
      ...(referenceImageUrl ? { reference_image_url: referenceImageUrl } : {})
    });
    return response.data;
  },

  async getSuggestions(): Promise<ApiResponse<AISuggestion[]>> {
    const response = await api.get('/social/suggestions');
    return response.data;
  },

  async discardSuggestion(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/social/suggestions/${id}`);
    return response.data;
  },

  async regenerateSuggestion(id: string): Promise<ApiResponse<AISuggestion>> {
    const response = await api.post(`/social/suggestions/${id}/regenerate`);
    return response.data;
  },

  // OAuth
  async getOAuthUrl(platform: string, projectId?: string): Promise<{ success: boolean; authUrl?: string; message?: string }> {
    const params = projectId ? { project_id: projectId } : {};
    const response = await api.get(`/oauth/${platform}/authorize`, { params });
    return response.data;
  },

  async getOAuthProviders(): Promise<ApiResponse<Array<{
    name: string;
    isConfigured: boolean;
    isConnected: boolean;
    connectedAccount: {
      username: string;
      connectedAt: string;
    } | null;
  }>>> {
    const response = await api.get('/oauth/providers');
    return response.data;
  },

  async disconnectOAuth(platform: string, projectId?: string): Promise<ApiResponse<void>> {
    const params: Record<string, string> = {};
    if (projectId) params.project_id = projectId;
    const response = await api.delete(`/oauth/${platform}`, { params });
    return response.data;
  },

  async refreshOAuthToken(platform: string): Promise<ApiResponse<{ expiresAt: string }>> {
    const response = await api.post(`/oauth/${platform}/refresh`);
    return response.data;
  },

  async refreshAccount(platform: string): Promise<ApiResponse<void>> {
    const response = await api.post(`/social/accounts/${platform}/refresh`);
    return response.data;
  },

  // AI Full Post Generation (text for each platform + optional image)
  async generateFullPost(params: {
    prompt: string;
    platforms: string[];
    tone?: string;
    targetAudience?: string;
    includeHashtags?: boolean;
    includeEmojis?: boolean;
    generateImage?: boolean;
    imageStyle?: string;
  }): Promise<ApiResponse<{
    text: string;
    hashtags: string[];
    imageUrl: string | null;
    platformContent: Record<string, { text: string; charCount: number; charLimit: number }>;
  }>> {
    const response = await api.post('/social/generate-full-post', params);
    return response.data;
  },

  // AI Image Generation (uses DALL-E via content endpoint)
  async generateImage(params: GenerateImageParams & { async?: boolean }): Promise<ApiResponse<AIGeneratedMedia> & { async?: boolean; jobId?: string }> {
    const response = await api.post('/content/generate-image', {
      prompt: params.prompt,
      style: params.style || 'realistic',
      size: params.aspectRatio === '9:16' ? '1024x1792' :
            params.aspectRatio === '16:9' ? '1792x1024' : '1024x1024',
      async: params.async ?? true, // Default to async mode
      ...(params.referenceImageUrl ? { reference_image_url: params.referenceImageUrl } : {})
    });
    return response.data;
  },

  // AI Video Generation
  async generateVideo(params: GenerateVideoParams): Promise<ApiResponse<AIGeneratedMedia>> {
    const response = await api.post('/ai/generate-video', params);
    return response.data;
  },

  // Check AI generation status (for async generation)
  async checkGenerationStatus(generationId: string): Promise<ApiResponse<AIGeneratedMedia>> {
    const response = await api.get(`/ai/generation/${generationId}/status`);
    return response.data;
  },

  // Get AI generation history
  async getAIGenerationHistory(params?: {
    type?: 'image' | 'video';
    limit?: number;
  }): Promise<ApiResponse<AIGeneratedMedia[]>> {
    const response = await api.get('/ai/generations', { params });
    return response.data;
  },

  // Page/Account Selection after OAuth
  async getAvailablePages(accountId: string): Promise<ApiResponse<{
    platform: string;
    pages: Array<{
      id: string;
      name: string;
      username?: string;
      picture?: string;
      followers?: number;
      subscribers?: number;
      type: string;
      category?: string;
      access_token?: string;
      facebook_page_id?: string;
      facebook_page_name?: string;
      urn?: string;
    }>;
    selected_page_id?: string;
    // facebook_ads specific fields
    fb_pages?: Array<{
      id: string;
      name: string;
      picture?: string;
      type: string;
      category?: string;
      access_token?: string;
    }>;
    selected_fb_page_id?: string;
    selected_fb_page?: {
      id: string;
      name: string;
      picture?: string;
      category?: string;
    };
  }>> {
    const response = await api.get(`/oauth/${accountId}/pages`);
    return response.data;
  },

  async selectPage(accountId: string, pageId: string): Promise<ApiResponse<SocialAccount>> {
    const response = await api.post(`/oauth/${accountId}/select-page`, { page_id: pageId });
    return response.data;
  },

  async selectFbPage(accountId: string, pageId: string): Promise<ApiResponse<SocialAccount>> {
    const response = await api.post(`/oauth/${accountId}/select-fb-page`, { page_id: pageId });
    return response.data;
  },

  // Upload image file (direct upload, up to 10MB)
  async uploadImage(file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<{
    url: string;
    type: 'image';
    width?: number;
    height?: number;
    fileSize: number;
  }>> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/media/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentage);
        }
      }
    });
    return response.data;
  },

  // Upload video file. Small clips go through the backend (instant). Anything
  // substantial goes through S3 MULTIPART (chunked, direct-to-S3): bypasses
  // Cloudflare's size/timeout limits and only loses one chunk on a network blip
  // instead of the whole file.
  async uploadVideo(
    file: File,
    onProgress?: (progress: number) => void,
    opts?: { onMultipartInit?: (info: { key: string; uploadId: string }) => void; signal?: AbortSignal }
  ): Promise<ApiResponse<{
    url: string;
    type: 'video';
    duration?: number;
    fileSize: number;
  }>> {
    const MULTIPART_THRESHOLD = 10 * 1024 * 1024; // 10MB
    if (file.size < MULTIPART_THRESHOLD) {
      return this.uploadVideoViaBackend(file, onProgress);
    }
    return this.uploadVideoViaMultipart(file, onProgress, opts);
  },

  // Upload video via backend (works up to Cloudflare limit ~100MB)
  async uploadVideoViaBackend(file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<{
    url: string;
    type: 'video';
    duration?: number;
    fileSize: number;
  }>> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/media/upload-video', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentage);
        }
      },
      timeout: 600000 // 10 minutes timeout for large videos
    });

    return response.data;
  },

  // Upload a video to S3 via MULTIPART (chunked, direct-to-S3). Resilient to
  // flaky connections (per-part retry), bypasses Cloudflare, no practical size
  // ceiling. On any failure it best-effort aborts so the uploaded parts don't
  // linger in the bucket.
  async uploadVideoViaMultipart(
    file: File,
    onProgress?: (progress: number) => void,
    opts?: { onMultipartInit?: (info: { key: string; uploadId: string }) => void; signal?: AbortSignal }
  ): Promise<ApiResponse<{
    url: string;
    type: 'video';
    duration?: number;
    fileSize: number;
  }>> {
    const PART_SIZE = 10 * 1024 * 1024; // 10MB (S3 min part is 5MB, except the last)
    const CONCURRENCY = 4;
    const MAX_RETRIES = 3;
    const contentType = file.type || 'video/mp4';

    // PUT one part to S3. Returns the part's ETag (needed to complete the upload).
    // Do NOT set Content-Type / x-amz-acl here: the UploadPart presigned URL does
    // not sign them and sending them can trigger SignatureDoesNotMatch. The
    // object's content-type and ACL were set when the upload was created.
    const putPartToS3 = (putUrl: string, blob: Blob, signal: AbortSignal | undefined, onPartProgress: (loaded: number) => void): Promise<string> =>
      new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', putUrl, true);
        xhr.timeout = 600000; // 10 min per part
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) onPartProgress(e.loaded); };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const etag = xhr.getResponseHeader('ETag');
            if (etag) resolve(etag);
            else reject(new Error('ETag ausente na resposta do S3 (o CORS precisa expor ETag).'));
          } else {
            reject(new Error(`Falha ao enviar parte (status ${xhr.status})`));
          }
        };
        xhr.onerror = () => reject(new Error('Erro de rede ao enviar parte do vídeo.'));
        xhr.ontimeout = () => reject(new Error('Tempo esgotado ao enviar parte do vídeo.'));
        if (signal) {
          if (signal.aborted) { xhr.abort(); reject(new DOMException('aborted', 'AbortError')); return; }
          signal.addEventListener('abort', () => { try { xhr.abort(); } catch { /* noop */ } reject(new DOMException('aborted', 'AbortError')); }, { once: true });
        }
        xhr.send(blob);
      });

    // 1. Start the multipart upload.
    const createRes = await api.post('/media/multipart/create', { filename: file.name, contentType, fileSize: file.size });
    if (!createRes.data?.success) {
      throw new Error(createRes.data?.message || 'Falha ao iniciar o upload do vídeo.');
    }
    const { uploadId, key, url } = createRes.data.data as { uploadId: string; key: string; url: string };
    // Surface the ids so the caller can abort on page-unload (where JS error
    // handling never gets a chance to run).
    opts?.onMultipartInit?.({ key, uploadId });

    const totalParts = Math.max(1, Math.ceil(file.size / PART_SIZE));
    const partNumbers = Array.from({ length: totalParts }, (_, i) => i + 1);

    // Internal controller: the moment ONE part permanently fails, abort all the
    // other in-flight part PUTs (otherwise they keep uploading to a key we're
    // about to abort). Chained to the caller's signal so an external cancel also
    // stops everything.
    const failController = new AbortController();
    const abortSignal = failController.signal;
    if (opts?.signal) {
      if (opts.signal.aborted) failController.abort();
      else opts.signal.addEventListener('abort', () => failController.abort(), { once: true });
    }

    try {
      // 2. Get a presigned PUT URL for every part.
      const signRes = await api.post('/media/multipart/sign-parts', { key, uploadId, partNumbers });
      if (!signRes.data?.success) throw new Error(signRes.data?.message || 'Falha ao preparar o upload.');
      const urlByPart = new Map<number, string>(
        (signRes.data.data.urls as Array<{ partNumber: number; url: string }>).map(u => [u.partNumber, u.url])
      );
      // Fail fast if the server didn't return a URL for every part — otherwise
      // we'd upload some parts and only discover the gap mid-flight.
      if (urlByPart.size !== totalParts || partNumbers.some(pn => !urlByPart.has(pn))) {
        throw new Error('Falha ao preparar o upload (URLs de partes incompletas).');
      }

      // 3. Upload parts with bounded concurrency, per-part retry and aggregate progress.
      const etags: Array<{ PartNumber: number; ETag: string }> = new Array(totalParts);
      const loadedPerPart: number[] = new Array(totalParts).fill(0);
      const reportProgress = () => {
        if (!onProgress || !file.size) return;
        const loaded = loadedPerPart.reduce((a, b) => a + b, 0);
        onProgress(Math.min(99, Math.round((loaded * 100) / file.size))); // cap at 99% until /complete
      };

      let nextIndex = 0;
      const worker = async (): Promise<void> => {
        for (;;) {
          if (abortSignal.aborted) throw new DOMException('aborted', 'AbortError');
          const idx = nextIndex++;
          if (idx >= totalParts) return;
          const partNumber = partNumbers[idx];
          const putUrl = urlByPart.get(partNumber)!;
          const blob = file.slice(idx * PART_SIZE, Math.min((idx + 1) * PART_SIZE, file.size));

          let attempt = 0;
          for (;;) {
            try {
              loadedPerPart[idx] = 0; // reset so a retry's progress doesn't jump backwards
              const etag = await putPartToS3(putUrl, blob, abortSignal, (loaded) => { loadedPerPart[idx] = loaded; reportProgress(); });
              etags[idx] = { PartNumber: partNumber, ETag: etag };
              loadedPerPart[idx] = blob.size;
              reportProgress();
              break;
            } catch (e) {
              if (abortSignal.aborted) throw e;
              attempt += 1;
              if (attempt >= MAX_RETRIES) { failController.abort(); throw e; } // stop the other workers
              await new Promise(r => setTimeout(r, 1000 * attempt)); // backoff
            }
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, totalParts) }, () => worker()));

      // 4. Assemble the parts into the final object. Re-check the cancel signal
      // first so we don't finalize an upload the user already cancelled.
      if (opts?.signal?.aborted) throw new DOMException('aborted', 'AbortError');
      const completeRes = await api.post('/media/multipart/complete', { key, uploadId, parts: etags });
      if (!completeRes.data?.success) throw new Error(completeRes.data?.message || 'Falha ao finalizar o upload.');
      onProgress?.(100);
      return { success: true, data: { url: completeRes.data.data?.url || url, type: 'video', fileSize: file.size } };
    } catch (err) {
      // Best-effort cleanup so the already-uploaded parts don't linger (and cost).
      this.abortMultipart(key, uploadId);
      throw err;
    }
  },

  // Abort a multipart upload (deletes uploaded parts). Best-effort / idempotent —
  // the S3 lifecycle rule sweeps anything this misses within a day.
  async abortMultipart(key: string, uploadId: string): Promise<void> {
    try {
      await api.post('/media/multipart/abort', { key, uploadId });
    } catch {
      /* swallow */
    }
  },

  // Upload media file (auto-detects type)
  async uploadMedia(file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<{
    url: string;
    thumbnailUrl?: string;
    type: 'image' | 'video';
    width?: number;
    height?: number;
    duration?: number;
    fileSize: number;
  }>> {
    const isVideo = file.type.startsWith('video/');

    if (isVideo) {
      const result = await this.uploadVideo(file, onProgress);
      return {
        success: result.success,
        data: {
          ...result.data,
          thumbnailUrl: undefined,
          width: undefined,
          height: undefined,
          duration: undefined
        }
      };
    } else {
      const result = await this.uploadImage(file, onProgress);
      return {
        success: result.success,
        data: {
          ...result.data,
          thumbnailUrl: undefined,
          duration: undefined
        }
      };
    }
  },

  // Published Posts with Metrics
  async getPublishedPosts(params?: { page?: number; limit?: number }): Promise<ApiResponse<{
    posts: Array<{
      id: string;
      content: string;
      platforms: string[];
      published_at: string;
      media_urls?: string[];
      external_ids?: Record<string, string>;
      publish_results?: Record<string, any>;
      metrics: {
        likes: number;
        comments: number;
        shares: number;
        saves: number;
        reach: number;
        impressions: number;
        engagement_rate: number;
      };
      last_synced?: string;
    }>;
    pagination: {
      total: number;
      page: number;
      totalPages: number;
      hasMore: boolean;
    };
  }>> {
    const response = await api.get('/social/posts/published', { params });
    return response.data;
  },

  async syncPostMetrics(postId: string): Promise<ApiResponse<{
    postId: string;
    metrics: {
      likes: number;
      comments: number;
      shares: number;
      saves: number;
      reach: number;
      impressions: number;
      engagement_rate: number;
    };
    lastSynced: string;
  }>> {
    const response = await api.post(`/social/posts/${postId}/sync-metrics`);
    return response.data;
  },

  async syncAllPostsMetrics(): Promise<ApiResponse<Array<{
    success: boolean;
    postId: string;
    metrics?: {
      likes: number;
      comments: number;
      shares: number;
      saves: number;
      reach: number;
      impressions: number;
      engagement_rate: number;
    };
    lastSynced?: string;
    error?: string;
  }>>> {
    const response = await api.post('/social/posts/sync-all-metrics');
    return response.data;
  },

  // Boost Post
  async boostPost(postId: string, config: {
    budget: number;
    duration_days: number;
    goal: string;
    targeting: any;
    ad_account_id?: string;
  }): Promise<ApiResponse<any>> {
    const response = await api.post(`/social/posts/${postId}/boost`, config);
    return response.data;
  },

  async stopBoost(postId: string): Promise<ApiResponse<any>> {
    const response = await api.post(`/social/posts/${postId}/boost/stop`);
    return response.data;
  },

  async resumeBoost(postId: string): Promise<ApiResponse<any>> {
    const response = await api.post(`/social/posts/${postId}/boost/resume`);
    return response.data;
  },

  async getBoostStatus(postId: string): Promise<ApiResponse<any>> {
    const response = await api.get(`/social/posts/${postId}/boost-status`);
    return response.data;
  },

  async searchBoostInterests(query: string): Promise<ApiResponse<Array<{ id: string; name: string; audience_size?: number }>>> {
    const response = await api.get('/social/boost/interests', { params: { q: query } });
    return response.data;
  },

  async searchBoostLocations(query: string): Promise<ApiResponse<Array<{ key: string; name: string; type: string; country_code?: string; country_name?: string }>>> {
    const response = await api.get('/social/boost/locations', { params: { q: query } });
    return response.data;
  },

  async getBoostAdAccounts(): Promise<ApiResponse<Array<{ id: string; account_id: string; name: string; currency: string }>>> {
    const response = await api.get('/social/boost/ad-accounts');
    return response.data;
  },

  /**
   * Ask Meta for the estimated reach of a targeting spec.
   * Returns `error` when Meta refused to compute (permissions/token/etc.) so
   * the UI can distinguish "0 people" (valid) from "couldn't compute".
   */
  async getBoostEstimatedReach(data: {
    ad_account_id: string;
    targeting: any;
  }): Promise<ApiResponse<{ users_lower_bound: number; users_upper_bound: number; error?: string; errorCode?: number | null }>> {
    const response = await api.post('/social/boost/estimated-reach', data);
    return response.data;
  },

  // Boost Content (from Content page - creates ad from scratch)
  async boostContent(data: {
    text?: string;
    title?: string;
    image_url?: string;
    link_url?: string;
    budget: number;
    duration_days: number;
    goal: string;
    targeting: any;
    ad_account_id?: string;
  }): Promise<ApiResponse<any>> {
    const response = await api.post('/social/boost/content', data);
    return response.data;
  },

  async stopContentBoost(campaignId: string): Promise<ApiResponse<any>> {
    const response = await api.post('/social/boost/content/stop', { campaign_id: campaignId });
    return response.data;
  },

  async resumeContentBoost(campaignId: string): Promise<ApiResponse<any>> {
    const response = await api.post('/social/boost/content/resume', { campaign_id: campaignId });
    return response.data;
  },

  async getContentBoostStatus(campaignId: string, adId: string): Promise<ApiResponse<any>> {
    const response = await api.post('/social/boost/content/status', { campaign_id: campaignId, ad_id: adId });
    return response.data;
  },

  // Fetch posts directly from connected social accounts (Instagram, Facebook, LinkedIn, etc.)
  async fetchExternalPosts(params?: { limit?: number; async?: boolean }): Promise<ApiResponse<{
    posts: Array<{
      id: string;
      external_id: string;
      content: string;
      platforms: string[];
      published_at: string;
      media_urls?: string[];
      media_type?: string;
      permalink?: string;
      external_ids?: Record<string, string>;
      platform: string;
      account_id: string;
      account_username: string;
      account_profile_image?: string;
      metrics: {
        likes: number;
        comments: number;
        shares: number;
        saves: number;
        reach: number;
        impressions: number;
        engagement_rate: number;
      };
    }>;
    accounts: Array<{
      id: string;
      platform: string;
      username: string;
      profile_image?: string;
      posts_fetched: number;
      error?: string;
    }>;
  }> & { async?: boolean; jobId?: string }> {
    const response = await api.get('/social/posts/external', {
      params: { ...params, async: params?.async ?? true }
    });
    return response.data;
  },

  // Ice Breakers
  async getIceBreakers(accountId: string): Promise<ApiResponse<any[]>> {
    const response = await api.get(`/social/accounts/${accountId}/ice-breakers`);
    return response.data;
  },

  async setIceBreakers(accountId: string, iceBreakers: Array<{ question: string; payload: string }>): Promise<ApiResponse<any>> {
    const response = await api.put(`/social/accounts/${accountId}/ice-breakers`, { ice_breakers: iceBreakers });
    return response.data;
  },

  async deleteIceBreakers(accountId: string): Promise<ApiResponse<any>> {
    const response = await api.delete(`/social/accounts/${accountId}/ice-breakers`);
    return response.data;
  }
};

export default socialService;
